import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Modal, Select, Checkbox } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import './LoginPage.css';
import { login as apiLogin, forgotPasswordByUsername, resetPasswordBySecurity } from '../services/api';
import { useMode } from '../context/ModeContext';

const { Option } = Select;

const DesktopLoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isRegister] = useState(false);
  // 移除忘记密码相关状态
  const navigate = useNavigate();
  const { setMode } = useMode();
  const [form] = Form.useForm();

  useEffect(() => {
    // 自动检测设备并设置默认模式
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const defaultMode = isMobile ? 'mobile' : 'desktop';

    // 从 localStorage 读取保存的模式，否则使用检测到的默认模式
    const savedMode = localStorage.getItem('mode') as 'desktop' | 'mobile' | null;
    const currentMode = savedMode || defaultMode;

    setMode(currentMode);
    form.setFieldsValue({ mode: currentMode });

    // 填充记住的凭据
    const savedUsername = localStorage.getItem('username');
    const savedPassword = localStorage.getItem('password');

    if (savedUsername) {
      form.setFieldsValue({ username: savedUsername });
    }
    if (savedPassword) {
      form.setFieldsValue({ password: savedPassword });
    }
  }, [form, setMode]);

  const onLoginFinish = async (values: any) => {
    setLoading(true);
    try {
      const data = await apiLogin(values.username, values.password);
      Cookies.set('token', data.token, { expires: 7 });

      const remember = values.remember !== false; // 默认记住
      if (remember) {
        localStorage.setItem('username', values.username);
        localStorage.setItem('password', values.password);
      } else {
        localStorage.removeItem('username');
        localStorage.removeItem('password');
      }
      localStorage.setItem('mode', values.mode);

      message.success('登录成功！');
      navigate('/dashboard');
    } catch (error) {
      const detail = (error as any)?.response?.data?.detail || '用户名或密码错误';
      Modal.error({ title: '登录失败', content: detail });
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (value: 'desktop' | 'mobile') => {
    setMode(value);
    localStorage.setItem('mode', value);
  };

  const onRegisterFinish = async (values: any) => {
    setLoading(true);
    try {
      const securityQuestions = [
        { question: values.securityQuestion1, answer: values.securityAnswer1 },
        { question: values.securityQuestion2, answer: values.securityAnswer2 },
        { question: values.securityQuestion3, answer: values.securityAnswer3 }
      ];

      console.log('前端发送的注册数据:', {
        username: values.username,
        security_questions: securityQuestions
      });

      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: values.username,
          email: `${values.username}@local.com`,
          password: values.password,
          security_questions: securityQuestions,
        }),
      });

      if (response.ok) {
        message.success('注册成功！请使用新账号登录');
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || '注册失败');
      }
    } catch (error) {
      message.error('注册失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  };

  // 移除忘记密码相关逻辑

  return (
    <div className="auth-container login-bg">
      <div className="auth-wrapper">
        <div className="auth-card">
            <div className="auth-header">
              {/* 去除“以图搜图”按钮/样式，仅保留标题与副标题 */}
              <h3 className="auth-title" style={{ marginTop: 8 }}>图像搜索系统</h3>
              <p className="auth-subtitle">请登录以继续使用</p>
            </div>
          
          <Form
            name="login"
            form={form}
            onFinish={onLoginFinish}
            autoComplete="off"
            className="auth-form"
          >
            <div className="form-group">
              <label className="form-label">用户名</label>
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input 
                  prefix={<UserOutlined className="form-icon" />}
                  placeholder="请输入用户名"
                  className="form-control"
                  size="large"
                />
              </Form.Item>
            </div>

            <div className="form-group">
              <label className="form-label">密码</label>
              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password 
                  prefix={<LockOutlined className="form-icon" />}
                  placeholder="请输入密码"
                  className="form-control"
                  size="large"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>
            </div>

            <div className="form-group">
              <label className="form-label">登录模式</label>
              <Form.Item name="mode" initialValue="desktop">
                <Select onChange={handleModeChange} size="large">
                  <Option value="desktop">电脑端</Option>
                  <Option value="mobile">手机端</Option>
                </Select>
              </Form.Item>
            </div>

            <div className="form-options">
              <Form.Item name="remember" valuePropName="checked" initialValue={true}>
                <Checkbox className="remember-me">记住密码</Checkbox>
              </Form.Item>
            </div>

            <Form.Item className="auth-submit">
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                className="auth-button"
                size="large"
                block
              >
                {isRegister ? '注册' : '登录'}
              </Button>
            </Form.Item>
          </Form>

          <div className="auth-footer">
            <div className="auth-switch">
              <p>
                <span>还没有账号？</span>
                <Link to="/register" className="switch-button">立即注册</Link>
              </p>
            </div>
            <p className="auth-copyright">
              © 2025 图像搜索系统
            </p>
          </div>
        </div>
      </div>

      {/* 移除忘记密码对话框 */}
    </div>
  );
};

const LoginPage: React.FC = () => {
  return (
    <div>
      <DesktopLoginPage />
    </div>
  );
}

export default LoginPage;
